import { useTranslation } from "react-i18next";
import useSnackBar from "../../../../components/Commons/SnackBar/useSnackBar";
import { useGridPreferences } from "../../../../hooks/useGridPreferences";
import { usePreferenceTypesStore } from "../../../../store/Preferences/PreferencesStore";
import { useCompanyStore } from "../../../../store/AuthStore";
import { useEffect, useState } from "react";
import GenericPromises from "../../../../api/GenericPromises";
import { MovementHistory } from "../../../../interfaces/Stocks/movementsHistory";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { PreferenceType } from "../../../../interfaces/Commons/Preferences/preferences";
import { useMovementsHistoryColumns } from "./columns";
import { Spinner } from "../../../../components/Commons/Spinner/Spinner";
import { ErrorDisplay } from "../../../../components/Commons/Error/ErrorDisplay";
import { Header } from "../../../../components/Header";
import { Box } from "@mui/material";
import { FilterTable } from "../../../../components/Filter";
import DataTableSticky from "../../../../components/Tables/GridTableMaterialUI/DataTableSticky";
import { DialogEntity } from "../../../../components/Dialogs/DialogEntity";
import { ViewMovementHistory } from "../view";


export const TableMovementsHistory = () => {
  const [t] = useTranslation("global");
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const {
    LoadColumnsOrder,
    LoadColumnsVisible,
    LoadColumnsSort,
    VerifyPreferenceOrderByResourceRoute,
    VerifyPreferenceVisualByResourceRoute,
    VerifyPreferenceSortByResourceRoute,
  } = useGridPreferences();
  const { preferenceTypes } = usePreferenceTypesStore.getState();
  const { columnsMovementsHistory } = useMovementsHistoryColumns();
  const { companyState } = useCompanyStore();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoadedMyLocalPreferences, setIsLoadedMyLocalPreferences] = useState(false);
  const { GenericGetResource } = GenericPromises();
  const [myPreferences, setMyPreferences] = useState({});
  const [mySortPreference, setMySortPreference] = useState([]);
  const [movementsHistoryData, setMovementsHistoryData] = useState<GridRowsProp>([]);
  const [columns, setColumns] = useState<GridColDef<MovementHistory>[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const initFilter = '/movementshistory?filter=';
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [openDialogView, setOpenDialogView] = useState(false);
  const [movementHistoryPayload, setMovementHistoryPayload] = useState<any>({});

  const myCustomColumns: GridColDef<any>[] = [
    { field: 'item_code', headerName: t("movementshistory.fields.item_code"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'item_description', headerName: t("movementshistory.fields.item_description"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'warehouse_code', headerName: t("movementshistory.fields.warehouse_code"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'warehouse_description', headerName: t("movementshistory.fields.warehouse_description"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'section_name', headerName: t("movementshistory.fields.section_name"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'section_description', headerName: t("movementshistory.fields.section_description"), headerClassName: 'header-grid-table', flex: 1 },
  ];

  const loadTablePreferences = async () => {
    // get preferencetypes in Store
    let hasPreferenceOrder: boolean = false;
    // if exists preferencetypes
    if (preferenceTypes.length) {
      // if exists order preferences
      let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
      if (myPreferencesColumnsOrder) {
        try {
          const preference = await VerifyPreferenceOrderByResourceRoute(
            "/movementshistory",
            myPreferencesColumnsOrder.preference_type_id ?? 0
          );
          if (preference) {
            let myColumns = await LoadColumnsOrder("/movementshistory", myCustomColumns, preference?.preference_value);
            setColumns(myColumns);
            hasPreferenceOrder = true;
          }
        } catch (error) {
          showSnackBar(`Error fetching column order preferences:${error}`, "error");
        }
      }

      let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
      // if exists visual preferences
      if (myPreferencesColumnsVisual) {
        try {
          const preference = await VerifyPreferenceVisualByResourceRoute(
            "/movementshistory",
            myPreferencesColumnsVisual.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsVisible("/movementshistory", preference);
            setMyPreferences(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column visual preferences:", error);
        }
      }

      let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
      // if exists sort preferences
      if (myPreferencesColumnsSort) {
        try {
          const preference = await VerifyPreferenceSortByResourceRoute(
            "/movementshistory",
            myPreferencesColumnsSort.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsSort("/movementshistory", preference);
            setMySortPreference(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column sort preferences:", error);
        }
      }

    }
    if (!hasPreferenceOrder) {
      setColumns(columnsMovementsHistory);
    }
    setIsLoadedMyLocalPreferences(true);
  }

  const GetMovementsHistory = () => {
    GenericGetResource(`/movementshistory`)
      .then((response) => {
        setMovementsHistoryData(response.data.data);
        setIsLoadingData(false);
      });
  }

  useEffect(() => {
    if (isLoadedMyLocalPreferences && movementsHistoryData) {
      setDataLoaded(true);
      setIsLoadingDataTable(true);
    }
  }, [movementsHistoryData, isLoadedMyLocalPreferences]);

  useEffect(() => {
    document.title = `${companyState?.comercial_name} - ${t("movementshistory.title")}`;
    const fetchData = async () => {
      try {
        await loadTablePreferences();
        GetMovementsHistory();
      } catch (err: any) {
        setErrorMessage(err);
        setHasError(true);
        showSnackBar(err, 'error');
      }
    };

    fetchData();
  }, []);

  return (
    <>
      {!dataLoaded && !hasError && <Spinner />}
      {hasError &&
        <ErrorDisplay
          errorMessage={errorMessage}
        />
      }
      {dataLoaded && !hasError &&
        <>
          <Header
            size="sm"
            title={t("movementshistory.title")}
            child={
              <Box display={"flex"} flexDirection={"row-reverse"}>
                <FilterTable
                  onFilterEmpty={GetMovementsHistory}
                  setIsLoadingDataTable={setIsLoadingData}
                  setDataTable={setMovementsHistoryData}
                  showSnackBar={showSnackBar}
                  columnsTable={columns}
                  initFilter={initFilter}
                  resourceRoute="/movementshistory"
                />
              </Box>
            }
          />
          {isLoadingDataTable &&
            <Box sx={{ height: 'calc(100vh - 180px)', width: '100%' }}>
              <DataTableSticky
                columns={columns}
                setColumns={setColumns}
                data={movementsHistoryData ?? []}
                entityId={"movement_history_id"}
                entity={`MovementsHistory`}
                preferences={myPreferences}
                namePreferences={"/movementshistory"}
                nameOrderColumns={"/movementshistory"}
                sortPreference={mySortPreference}
                isLoading={isLoadingData}
                isChildren
                setOpenDialog={setOpenDialogView}
                setDataRow={setMovementHistoryPayload}
              />
            </Box>
          }
          <DialogEntity
            content={
              <ViewMovementHistory
                movementHistoryPayload={movementHistoryPayload}
                setOpenDialog={setOpenDialogView}
              />
            }
            open={openDialogView}
            setOpen={setOpenDialogView}
            title={<Header title={t("movementshistory.title-view")} size='sm' />}
          />
        </>
      }
      <SnackbarComponent />
    </>
  )
}
